import React, { useState, useEffect } from "react";
import {Button, Card, Input, InputIcon, ProgressRing, Textarea} from "@salesforce/design-system-react";
import {fetchAccountVendingStatus} from "../../../../services/accountVending";
import {CONSTANT_ACCOUNT} from "../../../../constants/accountManagement";
import CustomSpinnerOverlay from "../../../../utils/components/spinner/custom_spinner_overlay";
import BulkAccessRequestStatusModal from "../../../../modals/BulkAccessRequestStatusModal";
import {ToastState} from "../../../../utils/types/toast";

type AccountVendingStatusProps = {
    isActive: boolean;
    setToast: React.Dispatch<React.SetStateAction<ToastState>>;
};

const AccountVendingStatus: React.FC<AccountVendingStatusProps> = ({ isActive, setToast }) => {
    const [isSearchCRTButtonDisabled, setIsSearchCRTButtonDisabled] = useState(true);
    const [searchClientErrorMessage, setSearchClientErrorMessage] = useState("");
    const [showSpinner, setShowSpinner] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [crtIdInput, setCrtIdInput] = useState("");
    const [accountCreationStatus, setAccountCreationStatus] = useState<Array<{
        crt_id: string;
        status: string;
        account_id: string;
        error_status: string;
    }>>([]);

    const clearSelections = () => {
        setAccountCreationStatus([])
    };

    useEffect(() => {
        if (isActive) {
            clearSelections();
            setCrtIdInput("");
        }
    }, [isActive]);

    useEffect(() => {
        setIsSearchCRTButtonDisabled(!validateCRT());
    }, [crtIdInput]);

    const validateCRT = () => {
        if (crtIdInput.length === 0) {
            setSearchClientErrorMessage("");
            return false;
        }
        if (crtIdInput.length === 36) {
            setSearchClientErrorMessage("");
            return true;
        }
        setSearchClientErrorMessage(CONSTANT_ACCOUNT.CRT_ID_ERROR_MESSAGE);
        return false;
    };

    const handleSearchClientRequestToken = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setCrtIdInput(event.target.value)
    };

    const handleFetchCRTStatus = async () => {
        setShowSpinner(true);
        clearSelections();
        try {
            const response = await fetchAccountVendingStatus(crtIdInput);

            const statuses = response.statuses.map((statusObj: any) => ({
                crt_id: response.client_request_token,
                status: statusObj.status,
                account_id: statusObj.aws_account_id || "",
                error_status: statusObj.result || ""
            }));

            setAccountCreationStatus(statuses);
            setShowConfirmationModal(true);
        } catch (error) {
            setAccountCreationStatus([]);
            setToast({
                isVisible: true,
                message: {
                    heading: "Error fetching status",
                    details: "No status found. Please get in touch with PCSK team if the issue persists"
                },
                variant: "error",
            })
        } finally {
            setShowSpinner(false);
        }
    };

    return (
        <Card heading={CONSTANT_ACCOUNT.ACCOUNT_STATUS}>
            <div className="slds-p-around_small">
                <div>
                    <Input
                        id="account_vending_crt_id"
                        label={CONSTANT_ACCOUNT.CRT_ID}
                        required
                        value={crtIdInput}
                        onChange={handleSearchClientRequestToken}
                        errorText={searchClientErrorMessage}
                        placeholder={CONSTANT_ACCOUNT.CRT_ID_PLACEHOLDER}
                        iconLeft={<InputIcon name="search" category="utility" />}
                    />
                </div>
                <div className="slds-p-top_medium slds-grid slds-grid_vertical-align-center">
                    <Button
                        label={CONSTANT_ACCOUNT.CHECK_STATUS}
                        variant="brand"
                        onClick={handleFetchCRTStatus}
                        disabled={isSearchCRTButtonDisabled}
                    />
                    {showSpinner && (
                        <div className="slds-m-left_medium">
                            <CustomSpinnerOverlay size={"small"}/>
                        </div>
                    )}
                </div>
                {accountCreationStatus.length > 1 ? (
                    <BulkAccessRequestStatusModal
                        isOpen={showConfirmationModal}
                        toggleModal={(isOpen) => setShowConfirmationModal(isOpen)}
                        data={accountCreationStatus}
                        crt_id={crtIdInput}
                    />
                ) : accountCreationStatus.length === 1 ? (
                    (() => {
                        const account = accountCreationStatus[0]; // Get the first result
                        return (
                            <>
                                {account.status.toLowerCase() === "processing" && (
                                    <div className="slds-p-top--medium">
                                        <div className="slds-grid">
                                            <div className="slds-m-left--x-small slds-m-top--xxx-small">
                                                {CONSTANT_ACCOUNT.STATUS}:
                                            </div>
                                            <div className="slds-m-left--x-small">
                                                <ProgressRing value={100} theme="warning"/>
                                            </div>
                                            <div className="slds-m-left--xx-small slds-m-top--xxx-small">
                                                {CONSTANT_ACCOUNT.PROCESSING}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {account.status.toLowerCase() === "success" && (
                                    <div className="slds-p-top--medium">
                                        <div className="slds-grid">
                                            <div className="slds-m-left--x-small slds-m-top--xxx-small">
                                                {CONSTANT_ACCOUNT.STATUS}:
                                            </div>
                                            <div className="slds-m-left--x-small">
                                                <ProgressRing value={100} theme="base"/>
                                            </div>
                                            <div className="slds-m-left--xx-small slds-m-top--xxx-small">
                                                {CONSTANT_ACCOUNT.SUCCESS}
                                            </div>
                                        </div>
                                        <Input label={CONSTANT_ACCOUNT.ACCOUNT_ID} value={account.account_id} disabled/>
                                    </div>
                                )}
                                {account.status.toLowerCase() === "failure" && (
                                    <div className="slds-p-top--medium">
                                        <div className="slds-grid">
                                            <div className="slds-m-left--x-small slds-m-top--xxx-small">
                                                {CONSTANT_ACCOUNT.STATUS}:
                                            </div>
                                            <div className="slds-m-left--x-small">
                                                <ProgressRing value={100} theme="expired"/>
                                            </div>
                                            <div className="slds-m-left--xx-small slds-m-top--xxx-small">
                                                {CONSTANT_ACCOUNT.FAILURE}
                                            </div>
                                        </div>
                                        {/*{account.account_id?.length !== 0 && <Input label={CONSTANT_ACCOUNT.ACCOUNT_ID} value={account.account_id} disabled/>}*/}
                                        <Textarea label={CONSTANT_ACCOUNT.ERROR} value={account.error_status} disabled/>
                                    </div>
                                )}
                                {account.status.toLowerCase() === "not found" && (
                                    <div className="slds-p-top--medium">
                                        <div className="slds-grid">
                                            <div className="slds-m-left--x-small slds-m-top--xxx-small">
                                                {CONSTANT_ACCOUNT.STATUS}:
                                            </div>
                                            <div className="slds-m-left--x-small">
                                                <ProgressRing value={100} theme="expired"/>
                                            </div>
                                            <div className="slds-m-left--xx-small slds-m-top--xxx-small">
                                                {CONSTANT_ACCOUNT.NOT_FOUND}
                                            </div>
                                        </div>
                                        {account.account_id?.length !== 0 && <Input label={CONSTANT_ACCOUNT.ACCOUNT_ID} value={account.account_id} disabled/>}
                                        <Textarea label={CONSTANT_ACCOUNT.ERROR} value={account.error_status} disabled/>
                                    </div>
                                )}
                            </>
                        );
                    })()
                ) : null}
            </div>
        </Card>
    );
};

export default AccountVendingStatus;